import { useCallback, useEffect, useRef, useState } from 'react'

import type { ErrorLike } from '@apollo/client'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { useQuery } from '@apollo/client/react'

import { ContentItemNotesSummaryQuery } from '~publish/hooks/notes/contentItemNotesSummary'
import type { ContentItemNotesSummary } from '~publish/hooks/notes/contentItemNotesSummary'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

export type ContentItemNotesThread =
  | { status: 'loading' }
  /** The summary is null or could not load: never an empty thread. */
  | { status: 'unavailable' }
  | {
      status: 'ready'
      summary: ContentItemNotesSummary
      isClosed: boolean
      canCreate: boolean
    }

type PusherContentItemEventData = { contentItemId: string }

/**
 * A real answer that the account cannot use the thread: the ContentItem is
 * gone, or access is denied. Anything else is a failed request.
 */
const isDenial = (error: ErrorLike | undefined): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some(
    ({ extensions }) =>
      extensions?.code === 'FORBIDDEN' || extensions?.code === 'NOT_FOUND',
  )

// A module constant: `usePusherEvent` rebinds when it gets a new array.
const LIFECYCLE_EVENTS = [
  PusherEvent.CONTENT_ITEM_UPDATED,
  PusherEvent.CONTENT_ITEM_DELETED,
]

/**
 * The host's view of a ContentItem thread's state. The generic Notes provider
 * never watches the ContentItem, so this hook does.
 *
 * It refetches `notesSummary` when a Pusher event names the ContentItem, on
 * the organization-wide channel, so it works with no card mounted; and when
 * the window regains focus. The Notes stream refetches it on reconnect. A
 * create that the server rejects as closed calls `markClosed`, which closes
 * the thread at once, before the refetch lands.
 *
 * Once a summary has loaded, a refetch that fails keeps the last one, so the
 * panel, and the Note the account is typing, stay. Only a null summary, a
 * denial, or a first load that fails shows the thread as unavailable.
 */
export function useContentItemNotesThread(contentItemId: string): {
  thread: ContentItemNotesThread
  markClosed: () => void
} {
  const { data, loading, error, refetch } = useQuery(
    ContentItemNotesSummaryQuery,
    {
      variables: { contentItemId },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
  )
  const [closedContentItemId, setClosedContentItemId] = useState<string | null>(
    null,
  )
  const lastSummaryRef = useRef<{
    contentItemId: string
    summary: ContentItemNotesSummary
  } | null>(null)

  const refresh = useCallback((): void => {
    refetch().catch(() => undefined)
  }, [refetch])

  usePusherEvent(
    LIFECYCLE_EVENTS,
    (event: PusherContentItemEventData): void => {
      if (event.contentItemId === contentItemId) refresh()
    },
  )

  useEffect(() => {
    window.addEventListener('focus', refresh)
    return (): void => window.removeEventListener('focus', refresh)
  }, [refresh])

  const markClosed = useCallback((): void => {
    setClosedContentItemId(contentItemId)
    refresh()
  }, [contentItemId, refresh])

  const unavailable =
    isDenial(error) || data?.contentItem?.notesSummary === null
  const loadedSummary = unavailable ? null : data?.contentItem?.notesSummary
  if (loadedSummary) {
    lastSummaryRef.current = { contentItemId, summary: loadedSummary }
  }
  const lastSummary =
    lastSummaryRef.current?.contentItemId === contentItemId
      ? lastSummaryRef.current.summary
      : null
  const summary = unavailable ? null : (loadedSummary ?? lastSummary)
  if (!summary) {
    return {
      thread:
        loading && !unavailable
          ? { status: 'loading' }
          : { status: 'unavailable' },
      markClosed,
    }
  }
  const isClosed = summary.isClosed || closedContentItemId === contentItemId
  return {
    thread: {
      status: 'ready',
      summary,
      isClosed,
      canCreate: summary.canCreate && !isClosed,
    },
    markClosed,
  }
}
